import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { profileTeams } from "actions/teams";
import {
  addMember,
  editMember,
  emailsList,
  schedulesList,
  teamMembers,
} from "actions/members";
import userProfile from "services/user.profile";
import ImageField from "pages/Profile/components/ImageField";
import FormField, { STYLE } from "components/FormField";
import Button, { SIZE, COLOR, STYLE as BTN_STYLE } from "components/Button";
import { Loading, SuccessCircle } from "components/assets/Icons";
import Checkbox from "components/Checkbox";
import Dropdown, { SIZE as FONT_SIZE } from "components/Dropdown";
import Message from "components/Message";
import Modal from "components/Modal";
import { ModalDelete, ModalAdd } from "../Modals";
import { createList } from "../../utils";

import styles from "./Form.module.scss";

const Form = ({ data, id, state, setPersonalInfo, employeesList, hasAccount }) => {
  const defaultValue = {
    firstName: data?.first_name,
    lastName: data?.last_name,
    name: { title: "", value: "" },
    email: { title: data?.email ?? "", value: "" },
    team: { title: data?.team ?? "", value: "" },
    schedule: { title: data?.schedule ?? "", value: "" },
    smartTelExt: data?.extension ?? undefined,
    bambooHrId: data?.bamboo_id ?? undefined,
    smartTelId: data?.usko_mysmart_tel_id ?? undefined,
  };
  const dispatch = useDispatch();
  const [value, setValue] = useState(defaultValue);
  const [disabled, setDisabled] = useState(false);
  const [checked, setChecked] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState({
    email: "",
    role: "",
    employeeId: "",
  });
  const [defaultList, setDefaultList] = useState([]);
  const [dropdownList, setDropdownList] = useState([]);
  const [dropdownValues, setDropdownValue] = useState({
    name: {
      title: "",
      value: "",
      photoUrl: "",
    },
    email: defaultValue.email,
    team: defaultValue.team,
    schedule: defaultValue.schedule,
  });
  const [showMessage, setShowMessage] = useState(false);
  const [disableButton, setDisableButton] = useState(false);
  const [currentModal, setCurrentModal] = useState("");
  const [teams, setTeams] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [emails, setEmails] = useState([]);
  const [error, setError] = useState(false);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [memberId, setMemberId] = useState();

  const employees = useSelector((state) => state.members);
  const teamsList = useSelector((state) => state.teams);

  useEffect(() => {
    dispatch(profileTeams);
    dispatch(schedulesList);
    dispatch(emailsList);
  }, [dispatch]);

  useEffect(() => {
    setTeams(createList(teamsList, "name"));
    setSchedules(createList(employees?.schedules, "name"));
    setEmails(createList(employees?.emails, "email"));
    setMemberId(employees?.memberId);
  }, [employees, teamsList]);

  const handleChange = () => {
    setChecked(!checked);
  };

  useEffect(() => {
    if ((value.bambooHrId ?? dropdownValues.name.value) === "") {
      setDisableButton(true);
    } else {
      setDisableButton(false);
    }
  }, [dropdownValues.name.value, value.bambooHrId]);

  const handleOnSubmit = (e) => {
    e.preventDefault();

    const name = dropdownValues.name?.title?.split(" ");
    const currentTeam = teams?.filter((item) => item.title === value.team);
    const currentSchedule = schedules?.filter((item) => item.title === value.schedule);
    const currentEmail = emails?.filter((item) => item.title === value.email);

    const formData = {
      first_name: value.firstName ?? name[0],
      last_name: value.lastName ?? name[1],
      email:
        dropdownValues.email?.value === ""
          ? currentEmail[0]?.value
          : dropdownValues.email?.value,
      team:
        dropdownValues.team?.value === ""
          ? currentTeam[0]?.value
          : dropdownValues.team?.value,
      schedule:
        dropdownValues.schedule?.value === ""
          ? currentSchedule[0]?.schedule
          : dropdownValues.schedule?.value,
      extension: value.smartTelExt,
      bamboo_id: value.bambooHrId ?? dropdownValues.name.value,
      usko_mysmart_tel_id: value.smartTelId,
    };

    state === "add" &&
      dispatch(addMember(formData)).then((res) => {
        setDisabled(false);
        memberId &&
          setModalData({
            email: dropdownValues.email?.title ?? value.email.title,
            id: memberId,
          });
        setShowMessage(true);
        dispatch(teamMembers);

        setTimeout(() => {
          setShowMessage(false);
          setCurrentModal("create");
          checked && memberId && setShowModal(true);
        }, 3000);
      });

    state === "edit" &&
      dispatch(editMember(id, formData)).then(() => {
        setPersonalInfo({
          first_name: value.firstName,
          last_name: value.lastName,
          is_active: data.is_active,
        });
        !showModal && setShowMessage(true);
        dispatch(teamMembers);
        setDisabled(false);

        setTimeout(() => setShowMessage(false), 3000);
      });
  };

  const handleCreate = (e) => {
    e.preventDefault();

    setCurrentModal("create");
    setModalData({
      email: dropdownValues.email?.title ?? value.email.title,
      id: data.id,
    });
    setShowModal(true);
  };

  const handleDelete = (e) => {
    e.preventDefault();

    setCurrentModal("delete");
    setModalData({
      id: data.id,
    });
    setShowModal(true);
  };

  useEffect(() => {
    const array = [];

    employeesList?.map(({ first_name, last_name, bamboo_id, url }) => {
      return array.push({
        title: `${first_name} ${last_name}`,
        value: bamboo_id,
        photoUrl: url,
      });
    });
    setDefaultList(array);
  }, [employeesList]);

  useEffect(() => {
    const result = defaultList?.filter(({ title }) => {
      return title.toLowerCase().startsWith(dropdownValues.name?.title?.toLowerCase());
    });

    if (dropdownValues.name?.title === "") {
      setDropdownList(defaultList);
      setDropdownValue({
        ...dropdownValues,
        name: { title: "", value: "", photoUrl: "" },
      });
    } else {
      result.length
        ? setDropdownList(result)
        : setDropdownList([{ title: "No options", value: "" }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropdownValues.name?.title, defaultList]);

  const handleChangeSmartTel = (e) => {
    const numberRegex = /^\d+$/;
    const countRegex = /^.{4,4}$/;

    setValue({ ...value, smartTelExt: e.target.value, smartTelId: "" });

    if (e.target.value === "") return;

    if (!numberRegex.test(e.target.value) && e.target.value !== "") {
      setError(true);
      setMessage("Smart Tel Ext must be a number");
    } else {
      setError(false);
      setMessage("");

      if (!countRegex.test(e.target.value) && e.target.value !== "") {
        setError(true);
        setLoading(false);
        setMessage("Enter 4 characters");
      } else {
        setLoading(true);
        userProfile.getSmartTelId(e.target.value).then(
          ({ data }) => {
            setLoading(false);
            if (data.smart_tel_id) {
              setValue({
                ...value,
                smartTelId: data.smart_tel_id,
                smartTelExt: e.target.value,
              });
              setMessage(<SuccessCircle fillColor="#4BB543" />);
            }
            data[404] && setMessage(data[404]);
          },
          (error) => {
            setMessage(error);
            setValue({ ...value, smartTelId: "" });
          },
        );
      }
    }
  };

  return (
    <>
      <form className={styles.form}>
        <div className={styles.personal_info}>
          <ImageField
            label="Profile Image"
            photoUrl={
              dropdownValues.name?.photoUrl ? dropdownValues.name?.photoUrl : data?.photo
            }
            className={styles.photo}
          />
          {state === "edit" ? (
            <>
              <FormField
                labelText="First Name"
                value={value.firstName}
                style={STYLE.LIGHT}
                className={styles.input}
                onChange={(e) => setValue({ ...value, firstName: e.target.value })}
                fieldName="firtsName"
              />
              <FormField
                labelText="Last Name"
                value={value.lastName}
                style={STYLE.LIGHT}
                className={styles.input}
                onChange={(e) => setValue({ ...value, lastName: e.target.value })}
                fieldName="lastName"
              />
            </>
          ) : (
            <Dropdown
              list={dropdownList}
              label="Name"
              className={styles.dropdown}
              value={dropdownValues}
              setValue={setDropdownValue}
              keyValue="name"
              withInput={true}
            />
          )}
          <Dropdown
            list={emails}
            label="Parsed GMail Email"
            className={styles.input}
            value={dropdownValues}
            setValue={setDropdownValue}
            keyValue="email"
            size={FONT_SIZE.MIDDLE}
          />
        </div>

        <div className={styles.schedule_info}>
          <Dropdown
            list={teams}
            label="Team"
            className={styles.input}
            value={dropdownValues}
            setValue={setDropdownValue}
            keyValue="team"
            size={FONT_SIZE.MIDDLE}
          />
          <Dropdown
            list={schedules}
            label="Schedule"
            className={styles.input}
            value={dropdownValues}
            setValue={setDropdownValue}
            keyValue="schedule"
            size={FONT_SIZE.MIDDLE}
          />
          <FormField
            labelText="Smart Tel Ext"
            value={value.smartTelExt ?? ""}
            style={STYLE.LIGHT}
            className={styles.input}
            onChange={handleChangeSmartTel}
            fieldName="smartTelExt"
            error={error}
            errorMessage={message}
            loading={loading}
          />
          <FormField
            labelText="BambooHR Id"
            value={
              (dropdownValues.name?.value
                ? dropdownValues.name.value
                : value.bambooHrId) ?? ""
            }
            style={STYLE.LIGHT}
            className={styles.input}
            disabled
          />
          <FormField
            labelText="Smart Tel Id"
            value={value.smartTelId ?? ""}
            style={STYLE.LIGHT}
            className={styles.input}
            disabled
          />
        </div>

        {state === "add" && (
          <Checkbox
            id="account"
            label="Create account for this user"
            value={checked}
            onChange={handleChange}
          />
        )}

        <div className={styles.group}>
          {state === "edit" && (
            <Button
              size={SIZE.MIDDLE}
              style={hasAccount ? COLOR.NONE : BTN_STYLE.INFO}
              className={styles.button}
              onClick={hasAccount ? handleDelete : handleCreate}
            >
              {hasAccount ? "Delete Account" : "Create Account"}
            </Button>
          )}

          <input
            size={SIZE.MIDDLE}
            type="submit"
            onClick={handleOnSubmit}
            className={styles.button}
            disabled={disabled || disableButton}
            value={disabled ? <Loading /> : "Save"}
          />
        </div>
      </form>

      {showModal && (
        <Modal
          setShowModal={setShowModal}
          className={styles.modal}
          content={
            currentModal === "create" ? (
              <ModalAdd data={modalData} setShowModal={setShowModal} />
            ) : currentModal === "delete" ? (
              <ModalDelete data={modalData} setShowModal={setShowModal} />
            ) : (
              <></>
            )
          }
        />
      )}

      {showMessage && <Message text="Your changes have been successfully saved!" />}
    </>
  );
};

export default Form;
